import { DCLeftNavModal, getDCLeftNavData, getWarehouseData } from "@NA/Services/ADIShopMyBranchService";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import Hidden from "@insite/mobius/Hidden";
import ADIShopMyBranchLeftNav from "@NA/Components/ShopMyBranch/ADIShopMyBranchLeftNav";
import { UnorderedList } from "@insite/mobius/Lists";
import getColor from "@insite/mobius/utilities/getColor";

export interface BranchInfoSectionStyles {
    branchInfoContainer?: InjectableCss;
    eachBox?: InjectableCss;
    headingStyle?: InjectableCss;
    innerTextStyle?: InjectableCss;
    contactNumberStyle?: InjectableCss;
}

export const branchInfoSectionStyles: BranchInfoSectionStyles = {
    branchInfoContainer: {
        css: css`
            > div:first-child {
                border-left: none;
            }
            > div:last-child {
                border-bottom: none;
            }
            display: flex;
            background-color: ${ADIColors.white2};
            width: 100%;
            min-width: 100%;
            border-radius: 4px;
            margin-bottom: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        padding: 15px 19px 12px 16px;
                    `,
                    css`
                        flex-direction: column;
                        padding: 15px 19px 12px 16px;
                    `,
                    css`
                        flex-direction: row;
                        padding: 19px 25px 18px 25px;
                    `,
                    css`
                        flex-direction: row;
                        padding: 19px 25px 18px 25px;
                    `,
                    css`
                        flex-direction: row;
                        padding: 20px 0 26px;
                    `,
                ])}
        `,
    },
    eachBox: {
        css: css`
            line-height: 1;
            padding-right: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        border-bottom: 1px solid ${ADIColors.gray19};
                        width: 100%;
                        padding: 0 0 13.5px 4px;
                        margin-bottom: 14.5px;
                        &:last-child {
                            margin-bottom: 0px;
                        }
                    `,
                    css`
                        border-bottom: 1px solid ${ADIColors.gray19};
                        width: 100%;
                        padding: 0 0 13.5px 4px;
                        margin-bottom: 14.5px;
                        &:last-child {
                            margin-bottom: 0px;
                        }
                    `,
                    css`
                        border-left: 1px solid ${ADIColors.gray19};
                        width: 33.33%;
                        padding-left: 20px;
                    `,
                    css`
                        border-left: 1px solid ${ADIColors.gray19};
                        width: 33.33%;
                        padding-left: 34px;
                    `,
                    css`
                        border-left: 1px solid ${ADIColors.gray19};
                        width: 33.33%;
                        padding-left: 34px;
                    `,
                ])}
        `,
    },
    headingStyle: {
        css: css`
            font-size: 24px;
            line-height: normal;
            color: ${ADIColors.darkBlue1};
            font-weight: bold;
            margin-bottom: 6px;
            text-transform: capitalize;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 16px;
                        margin-bottom: 6px;
                    `,
                    css`
                        font-size: 16px;
                        margin-bottom: 6px;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    innerTextStyle: {
        css: css`
            padding: 0;
            line-height: 1;
            margin-bottom: 10px;
            font-size: 16px;
            color: #2d2926; /* not present in ADIColors */
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                        margin-bottom: 6px;
                    `,
                    css`
                        font-size: 12px;
                        margin-bottom: 6px;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    contactNumberStyle: {
        css: css`
            font-weight: bold;
        `,
    },
};

const defaultBranchInfo = {
    name: "",
    code: "",
    attributeid: "",
    address1: "",
    address2: "",
    phone: "",
    city: "",
    state: "",
    zipCode: "",
    fax: "",
    manager: "",
    hours: "",
    email: "",
};

export interface ShopMyBranchDCLeftNavStyles {
    leftNavContainer?: InjectableCss;
    listStyle?: InjectableCss;
    mobileListStyle?: InjectableCss;
    innerUnorderedListStyle?: InjectableCss;
    innerListItemStyle?: InjectableCss;
    mobileContainer?: InjectableCss;
    toggleOptionColor?: InjectableCss;
}

const mapStateToProps = (state: ApplicationState) => ({
    currentPage: getCurrentPage(state),
    breadcrumbLinks: state.components.breadcrumbs.links,
});

const defaultDCData: DCLeftNavModal = {
    attributeId: "",
    dc: "",
    brand: [],
    category: [],
    mwts: [],
};

type Props = ReturnType<typeof mapStateToProps>;

const ShopMyBranchInfo = ({ currentPage, breadcrumbLinks }: Props) => {
    const [branchInfo, setBranchInfo] = useState(defaultBranchInfo);
    const styles = branchInfoSectionStyles;
    const [isMobile, setIsMobile] = useState(false);
    const [dcLeftNavData, setDcLeftNavData] = useState<DCLeftNavModal>(defaultDCData);
    const [expandAllOptions, setExpandAllOptions] = useState<boolean>(true);

    const shopMyBranchDCLeftNavStyles: ShopMyBranchDCLeftNavStyles = {
        leftNavContainer: {
            css: css`
                min-width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            max-width: 100%;
                            width: 100% !important;
                            padding: 20px 0px 10px 0;
                        `,
                        css`
                            max-width: 100%;
                            width: 100% !important;
                            padding: 20px 0px 10px 0;
                        `,
                        css`
                            max-width: 100%;
                            width: 100% !important;
                            padding: 30px 0 10px 0;
                            display: none;
                        `,
                        css`
                            max-width: 100%;
                            width: 100% !important;
                            padding: 0 50px 0 0;
                        `,
                        css`
                            max-width: 100%;
                            width: 100% !important;
                            padding: 0 50px 0 0;
                        `,
                    ])}
                .shop-my-branch-header {
                    margin-bottom: 9px;
                    font-size: 16px;
                    font-weight: bold;
                    line-height: normal;
                    letter-spacing: normal;
                    color: ${ADIColors.darkBlue1};
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                padding: 19px 12px 18px 12px;
                            `,
                            css`
                                padding: 19px 12px 18px 12px;
                            `,
                            css``,
                            css``,
                            css``,
                        ])}
                }
                .parent-menu {
                    cursor: pointer;
                    border-top: 1px solid ${ADIColors.gray2}!important;
                    font-size: 14px;
                    font-weight: bold;
                    line-height: normal;
                    letter-spacing: normal;
                    color: ${getColor("text.main")};
                    padding-bottom: 15px;
                    padding-top: 17px;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                &:first-child {
                                    border-top: none;
                                }
                            `,
                            css`
                                &:first-child {
                                    border-top: none;
                                }
                            `,
                            css``,
                            css``,
                            css``,
                        ])}
                }
            `,
        },
        listStyle: {
            css: css`
                > ul {
                    list-style: none !important;
                    padding: 0 !important;
                    margin: 0 !important;
                }
            `,
        },
        mobileListStyle: {
            css: css`
                > ul {
                    display: ${expandAllOptions ? "none" : "block"};
                    list-style: none !important;
                    padding: 0 !important;
                    margin: 0 !important;
                }
            `,
        },
        innerUnorderedListStyle: {
            css: css`
                max-height: 393px;
                width: 100% !important;
                padding-right: 22px !important;
                overflow: auto !important;
                &::-webkit-scrollbar {
                    width: 5px;
                }
                &::-webkit-scrollbar-thumb {
                    background: ${ADIColors.gray7};
                    border-radius: 10px;
                }
            `,
        },
        innerListItemStyle: {
            css: css`
                margin-bottom: 12px;
                > a {
                    font-size: 14px;
                    font-weight: normal;
                    line-height: normal;
                    letter-spacing: normal;
                    > span {
                        color: ${ADIColors.black} !important;
                    }
                }
            `,
        },
        mobileContainer: {
            css: css`
                display: flex;
                background: ${ADIColors.gray6};
                justify-content: space-between;
                padding: 15px 0;
            `,
        },
        toggleOptionColor: {
            css: css`
                color: ${ADIColors.blue5};
            `,
        },
    };

    const onWindowResize = () => {
        if (window.innerWidth > 1024) {
            setIsMobile(false);
        } else {
            setIsMobile(true);
        }
    };

    useEffect(() => {
        window.addEventListener("resize", onWindowResize);
        onWindowResize();
        fetchData();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);

    const fetchData = async () => {
        const response = await getWarehouseData();
        setBranchInfo(response);
    };

    const mailAddress = `mailto:${branchInfo?.email}`;

    return (
        <>
            <StyledWrapper {...styles.branchInfoContainer}>
                <StyledWrapper className="branch" {...styles.eachBox}>
                    <StyledWrapper className="branchHeading" {...styles.headingStyle}>
                        {branchInfo?.name?.trim().toLowerCase()} {translate("Branch")}
                    </StyledWrapper>
                    <StyledWrapper className="branch--innetText">
                        <Typography variant="p" {...styles.innerTextStyle}>
                            {branchInfo?.address1?.trim()}
                        </Typography>
                        <Typography variant="p" {...styles.innerTextStyle}>
                            {branchInfo?.address2?.trim()}
                        </Typography>
                        <Typography variant="p" {...styles.innerTextStyle}>
                            {branchInfo?.city?.trim()}, {branchInfo?.state?.trim()}, {branchInfo?.zipCode?.trim()}
                        </Typography>
                        <Link href="/dealerlocator" target="_blank">
                            {translate("Directions")}
                        </Link>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper className="manager" {...styles.eachBox}>
                    <StyledWrapper className="branchHeading" {...styles.headingStyle}>
                        {translate("Manager")}
                    </StyledWrapper>
                    <StyledWrapper className="branch--innetText">
                        <Typography variant="p" {...styles.innerTextStyle}>
                            {branchInfo.manager}
                        </Typography>
                        <Typography variant="p" {...styles.innerTextStyle}>
                            {translate("Phone")}:&nbsp;
                            <span {...styles.contactNumberStyle}>{branchInfo.phone}</span>
                        </Typography>
                        <Typography variant="p" {...styles.innerTextStyle}>
                            {translate("Email")}:&nbsp;
                            <Link href={mailAddress}>{branchInfo.email}</Link>
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper className="hours" {...styles.eachBox}>
                    <StyledWrapper className="branchHeading" {...styles.headingStyle}>
                        {translate("Hours")}
                    </StyledWrapper>
                    <StyledWrapper className="branch--innetText">
                        <Typography variant="p" {...styles.innerTextStyle}>
                            {translate("Mon-Fri: 7:30am-5:30pm")}
                        </Typography>
                        <Typography variant="p" {...styles.innerTextStyle}>
                            {translate("May vary due to events/holidays.")}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {isMobile && (
                <StyledWrapper {...shopMyBranchDCLeftNavStyles.leftNavContainer}>
                    <StyledWrapper className="shop-my-branch-header" {...shopMyBranchDCLeftNavStyles.mobileContainer}>
                        {translate("Shop My Branch")}
                        <StyledWrapper>
                            <Typography
                                {...shopMyBranchDCLeftNavStyles.toggleOptionColor}
                                onClick={() => {
                                    setExpandAllOptions(!expandAllOptions);
                                }}
                            >
                                {!expandAllOptions && translate("-Collapse")}
                            </Typography>
                            <Typography
                                {...shopMyBranchDCLeftNavStyles.toggleOptionColor}
                                onClick={() => {
                                    setExpandAllOptions(!expandAllOptions);
                                }}
                            >
                                {expandAllOptions && translate("+Expand")}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper
                        {...shopMyBranchDCLeftNavStyles.listStyle}
                        {...shopMyBranchDCLeftNavStyles.mobileListStyle}
                    >
                        <UnorderedList>
                            <ADIShopMyBranchLeftNav
                                title="Categories"
                                leftNavData={dcLeftNavData.category}
                            ></ADIShopMyBranchLeftNav>
                            <ADIShopMyBranchLeftNav
                                title="Brands"
                                leftNavData={dcLeftNavData.brand?.splice(0, 15)}
                                showMoreButton={true}
                            ></ADIShopMyBranchLeftNav>
                            <ADIShopMyBranchLeftNav
                                title="Deals And Promotions"
                                leftNavData={dcLeftNavData.mwts}
                            ></ADIShopMyBranchLeftNav>
                        </UnorderedList>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ShopMyBranchInfo),
    definition: {
        group: "Basic",
        displayName: "Shop My Branch Info",
    },
};

export default widgetModule;
